import { System } from '@ecs/System';
import { Position } from '@components/Position';
import { Velocity } from '@components/Velocity';
import { CirclerAI } from '@components/CirclerAI';
import { Tag } from '@components/Tag';

/**
 * Orbits the nearest player. Each frame the ship aims for a point on the orbit
 * circle slightly ahead of its current bearing, so it spirals in from afar and
 * then keeps circling.
 */
export class CirclerAISystem extends System {
  update(_deltaTime: number): void {
    const players = this.getEntitiesWithComponents(Position, Tag).filter(
      e => e.getComponent(Tag)!.value === 'player'
    );
    if (players.length === 0) return;

    for (const entity of this.getEntitiesWithComponents(Position, Velocity, CirclerAI, Tag)) {
      if (entity.getComponent(Tag)!.value !== 'enemy') continue;

      const position = entity.getComponent(Position)!;
      const velocity = entity.getComponent(Velocity)!;
      const ai = entity.getComponent(CirclerAI)!;

      let target: Position | null = null;
      let minDistance = Infinity;
      for (const player of players) {
        const playerPos = player.getComponent(Position)!;
        const distance = Math.hypot(playerPos.x - position.x, playerPos.y - position.y);
        if (distance < minDistance) {
          minDistance = distance;
          target = playerPos;
        }
      }
      if (!target) continue;

      const bearing = Math.atan2(position.y - target.y, position.x - target.x);
      const lead = Math.min(0.8, Math.max(0.2, ai.circleSpeed * 25));
      ai.currentAngle = bearing + lead * ai.direction;

      const goalX = target.x + Math.cos(ai.currentAngle) * ai.circleRadius;
      const goalY = target.y + Math.sin(ai.currentAngle) * ai.circleRadius;
      const desiredAngle = Math.atan2(goalY - position.y, goalX - position.x);

      let angleDiff = desiredAngle - velocity.angle;
      while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
      while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;
      velocity.angle += Math.max(-velocity.turnRate, Math.min(velocity.turnRate, angleDiff));

      velocity.speed = Number.isFinite(velocity.maxSpeed) ? velocity.maxSpeed : 1;
    }
  }
}
